import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from '../routing';
import { documentsApi, formatCurrency } from '../utils/api';
import { toast } from '../components/Toast';
import { formatDateShort, formatUploadedFull } from '../utils/dateFormat';
import { track, EVENTS } from '../utils/analytics';
import Icon from '../components/Icon';
import EmptyState from '../components/EmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { useConfirm } from '../utils/useConfirm';
import DeleteDocumentDialog from '../components/DeleteDocumentDialog';
import DuplicateCompareDialog from '../components/DuplicateCompareDialog';
import { keepDraftDeleteContext, keepDraftDeleteQuestion, keepDraftDeleteWarning } from '../utils/duplicateCompare';
import { useAuth } from '../context/AuthContext';
import Switch from '../components/Switch';
import StatInfo from '../components/StatInfo';
import { sortDraftQueue, supplierGroupCount } from '../utils/draftQueue';
import { readDraftOrder, writeDraftOrder } from '../utils/draftQueueOrder';
import { reviewSecondsSince } from '../utils/reviewTiming';
import { readGatePreview, writeGatePreview } from '../utils/gatePreview';
import GateMark from '../components/GateMark';
import { gateCounts, filterDraftQueue, readGateFilter, writeGateFilter, rememberGateColors, gateColorScope, enterGateColorScope, gateAnalytics, GATE_MARKS, GREEN_TITLE, APPROVE_ALL_FILTERED_TITLE } from '../utils/gateMark';

// The row ⋮ panel's place in the window: under the trigger, its left edge
// on the trigger's (the panel opens toward the page's start in RTL, like
// .page-menu-panel), kept 8px inside the viewport, and above the trigger
// when there is not room for it below.
const ROW_MENU_W = 220;
const ROW_MENU_H = 210;
export function rowMenuStyle(rect, vw = window.innerWidth, vh = window.innerHeight) {
  const left = Math.max(8, Math.min(rect.left, vw - ROW_MENU_W - 8));
  const below = rect.bottom + 6 + ROW_MENU_H <= vh;
  return below
    ? { position: 'fixed', left, top: rect.bottom + 6, right: 'auto', minWidth: 200 }
    : { position: 'fixed', left, bottom: vh - rect.top + 6, top: 'auto', right: 'auto', minWidth: 200 };
}

// Admin-only review queue for documents saved with status='טיוטה'.
// Each row deep-links to /document/:id with autoEdit so the existing
// DocumentViewPage drives the actual review — this page is just the list
// + the approve/delete actions that only make sense for drafts.
// What מיין לפי ספק does — sortDraftQueue's rule, behind the ? beside the
// switch (owner, 22/10/2026: a switch with an explanation, nothing more).
const ORDER_INFO = (
  <>
    הטיוטות מסודרות ספק אחר ספק, כך שמסמכים של אותו ספק מגיעים ברצף:
    <ul className="stat-info-list">
      <li>קודם טיוטות בלי ספק</li>
      <li>אחר כך הספק שממתין הכי הרבה זמן</li>
      <li>בכל ספק: חשבוניות, תעודות משלוח, מרכזות, זיכויים — מהתאריך הישן לחדש</li>
    </ul>
    <p className="stat-info-note">אשר והבא עובר לפי אותו סדר. כבוי: לפי סדר ההעלאה, האחרונות קודם.</p>
  </>
);

export default function DraftsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [drafts, setDrafts]   = useState([]);
  // The queue order (#313): one supplier at a time by default, or the
  // server's upload order. The review screen reads the same choice, so the
  // list and אשר והבא never disagree.
  const [order, setOrder] = useState(() => readDraftOrder(user?.id));
  const bySupplier = order === 'supplier';
  const setBySupplier = (on) => {
    const next = on ? 'supplier' : 'upload';
    setOrder(next);
    writeDraftOrder(user?.id, next);
  };
  const sorted = useMemo(() => sortDraftQueue(drafts, order), [drafts, order]);
  // The toggle means nothing with a single supplier in the queue.
  const showOrderToggle = supplierGroupCount(drafts) >= 2;
  const [askConfirm, confirmDialog] = useConfirm();
  // The queue's own decisions (the ✓, אשר הכל, מחק) are timed from the later of
  // the page load and the reviewer's last decision here — the reviewer
  // seconds per decided draft KPI counts them (stage 3 §1, #330). Opening a
  // draft leaves the page, so coming back starts a fresh load. The clock
  // moves only when the server reports a decision: a 200 that decided
  // nothing (a draft another reviewer already finalized, an אשר הכל that held
  // every row) wrote no review row, so its seconds belong to the next one.
  const lastActionAt = useRef(Date.now());
  const queueSeconds = () => reviewSecondsSince(lastActionAt.current);
  const markAction = () => { lastActionAt.current = Date.now(); };
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy]       = useState(false);
  // Filter: when on, only show drafts flagged duplicate_warning=1.
  const [onlyDupes, setOnlyDupes] = useState(false);
  // The filter applies only while a flagged draft exists. Its button renders
  // only then too, so once the last duplicate is deleted (the compare flow's
  // "זה כפיל · מחק") a still-on filter would hide every row with no button
  // left to turn it off.
  const dupeCount = drafts.filter(d => d.duplicate_warning).length;
  const filteringDupes = onlyDupes && dupeCount > 0;
  // The draft gate (stage 3 / C, #332): לתיקון / לבדיקה filter the queue, in
  // its order; the green count is a plain number, never a filter (spec §2).
  // The filter lives in module memory (utils/gateMark.js) so the review
  // screen's אשר והבא / דלג / מחק והבא stay inside it (owner answer 6). One
  // filter at a time, the duplicates toggle included: each works on its own.
  // Like the duplicates filter, it applies only while a draft matches.
  // A login or preview change starts a new display context (no colours, no
  // filter carried over) before the filter is read.
  const [gateFilter, setGateFilterState] = useState(() => { enterGateColorScope(gateColorScope(user?.id, readGatePreview(user?.id))); return readGateFilter(); });
  const counts = useMemo(() => gateCounts(drafts), [drafts]);
  const filteringGate = gateFilter != null && counts[gateFilter] > 0;
  const setGateFilter = (f) => { writeGateFilter(f); setGateFilterState(f); };
  // Any filter hides rows, and אשר הכל would approve them unseen — the
  // duplicates filter included (its rows are the ones the server holds back,
  // so it approved only what the screen hid; #332 design review).
  const filtering = filteringGate || filteringDupes;
  // The two halves of a combined החברה המרכזית sheet (#367): the server's אשר הכל
  // leaves these behind, so the button counts only what it will approve.
  const waitingCount = drafts.filter(d => d.empty_return || d.return_pending).length;
  const approvable = drafts.length - waitingCount;
  const toggleGateFilter = (f) => {
    const on = gateFilter !== f;
    setGateFilter(on ? f : null);
    if (on) setOnlyDupes(false);
    track(EVENTS.GATE_FILTER_USED, { filter: f, on, count: counts[f], queue_size: drafts.length });
  };
  const toggleDupes = () => {
    if (!onlyDupes) setGateFilter(null);
    setOnlyDupes(v => !v);
  };
  // A filter whose last draft was decided is dropped, so it never comes back
  // on by itself when a new red or yellow arrives.
  useEffect(() => {
    if (!loading && gateFilter != null && counts[gateFilter] === 0) setGateFilter(null);
  }, [loading, gateFilter, counts]); // eslint-disable-line react-hooks/exhaustive-deps
  // The duplicate compare screen (#234), opened from a flagged row without
  // entering the draft: { draft } once GET /documents/:id (which carries
  // duplicate_of) is back. `deleting` may carry `fromCompare` so the reason
  // chip is pre-picked.
  const [comparing, setComparing] = useState(null);
  // "השאר את הטיוטה · מחק את הקיים": { draft, existing } while the reason
  // dialog for the EXISTING document is up; once it is deleted the draft
  // opens for editing, like ערוך ואשר.
  const [deletingExisting, setDeletingExisting] = useState(null);
  // Which row's ⋮ menu is open (owner, 12/10/2026): the row shows ערוך ואשר
  // and an icon-only מחק; everything, including אשר and השווה, is in the ⋮.
  // { id, style } — the panel is placed against the WINDOW (position: fixed)
  // from the trigger's rect, not inside the cell: on a phone the table sits
  // in .table-scroll-mobile (overflow-x: auto, which also clips vertically),
  // and an absolutely placed panel on the last row was cut off entirely.
  const [rowMenu, setRowMenu] = useState(null);
  const toggleRowMenu = (id, trigger) => {
    if (rowMenu?.id === id) { setRowMenu(null); return; }
    setRowMenu({ id, style: rowMenuStyle(trigger.getBoundingClientRect()) });
  };
  // A fixed panel would float away from its row on scroll or resize, so the
  // menu simply closes — the same as a click outside.
  useEffect(() => {
    if (!rowMenu) return undefined;
    const close = () => setRowMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [rowMenu]);

  // The owner's preview of the gate colours before go-live (utils/gatePreview.js):
  // the ⋮ switch shows only when the server says this login may preview.
  const [previewAllowed, setPreviewAllowed] = useState(false);
  const [gatePreview, setGatePreviewState] = useState(() => readGatePreview(user?.id));
  // What every load reads AT THE MOMENT IT STARTS: an action that finishes
  // after the switch moved (an approval, a delete) calls the load it closed
  // over, and must still ask for the current choice (Codex review of #343).
  const gatePreviewRef = useRef(gatePreview);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const [pageMenuOpen, setPageMenuOpen] = useState(false);
  useEffect(() => {
    let live = true;
    // A promise from the first step: a missing endpoint never breaks the queue.
    Promise.resolve()
      .then(() => documentsApi.gatePreview())
      .then(r => { if (live) setPreviewAllowed(!!r?.data?.allowed); })
      .catch(() => { if (live) setPreviewAllowed(false); });
    return () => { live = false; };
  }, []);
  const toggleGatePreview = () => {
    const on = !gatePreview;
    writeGatePreview(user?.id, on);
    gatePreviewRef.current = on;
    setGatePreviewState(on);
    // A new display context: forget the colours and the filter at once, and —
    // switching off — take the colours off the rows on screen before the
    // reload answers (Codex review of #343, preview delta).
    enterGateColorScope(gateColorScope(user?.id, on));
    setGateFilterState(null);
    if (!on) setDrafts(ds => ds.map(d => ({ ...d, gate_state: d.gate_state === 'none' ? 'none' : 'pending', gate_color: null, gate_reasons: null })));
  };
  // Only the latest load commits: a preview answer that lands after the
  // switch moved on must not repaint the colours (Codex review of #343).
  const loadGenRef = useRef(0);

  const load = useCallback(async () => {
    // A stale callback after the page has gone starts nothing.
    if (!mountedRef.current) return;
    const preview = gatePreviewRef.current;
    const gen = ++loadGenRef.current;
    const current = () => gen === loadGenRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await documentsApi.getDrafts({ gatePreview: preview });
      if (!current()) return;
      const rows = Array.isArray(r.data) ? r.data : [];
      rememberGateColors(rows, gateColorScope(user?.id, preview));
      setDrafts(rows);
    } catch {
      // Was a toast only, which left "אין טיוטות ממתינות" on screen — the
      // one message a reviewer of a draft queue must never be told falsely.
      if (current()) setLoadError(true);
    } finally {
      if (current()) setLoading(false);
    }
  }, [user?.id]);

  // Leaving the page (a logout included) supersedes every load still in
  // flight: an answer landing after it must never write the shared colour
  // memory for the login that left (Codex review of #343, preview fixes).
  // A preview toggle reloads too: load reads the choice from gatePreviewRef,
  // so the dependency that triggers it lives here, not on load.
  useEffect(() => {
    load();
    return () => { loadGenRef.current += 1; };
  }, [load, gatePreview]);

  const openReview = (d) => {
    // autoEdit + autoOpenFiles → land in the editable items table with the
    // original file already expanded, no extra clicks needed.
    // entry: how the reviewer got there, reported back with the decision
    // (utils/reviewTiming.js) — the review screen passes approve_next on its own.
    navigate(`/document/${d.id}`, { state: { autoEdit: true, autoOpenFiles: true, entry: 'queue_edit' } });
  };

  const openCompare = async (d) => {
    setBusy(true);
    try {
      const r = await documentsApi.getOne(d.id);
      if (!r.data?.duplicate_of) {
        // The flag was stale: the other document is gone (deleted) or the
        // draft was fixed meanwhile. Show the queue as it is now.
        toast('המסמך הקיים כבר לא נמצא — הרשימה רועננה');
        load();
        return;
      }
      setComparing({ draft: r.data });
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בטעינת המסמך להשוואה', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (d) => {
    // A חשבונית מרכזת is never approved straight from the list. Approving it
    // here would skip the only prompt that links its תעודות משלוח, and an
    // unlinked note is NOT written down — server/lib/amountOwed.js zeroes a
    // note only once `invoice_delivery_links` says the מרכזת covers it, so
    // both documents then count in full and the supplier's balance is
    // overstated by the notes' value. The linking modal lives on the document
    // screen, so send the click there: still one click for the reviewer, it
    // just lands on the surface that can make the offer.
    if (d.doc_type === 'חשבונית מרכזת') { openReview(d); return; }
    const review = { review_seconds: queueSeconds() };
    setBusy(true);
    try {
      const r = await documentsApi.approve(d.id, { entry: 'queue_quick', review });
      if (r?.data?.approved) markAction();
      const changed = r?.data?.review?.fields_changed_count;
      track(EVENTS.DOCUMENT_APPROVED, {
        source: 'drafts_single', doc_type: d.doc_type, advanced_to_next: false, was_duplicate: !!d.duplicate_warning,
        ...gateAnalytics(d),
        ...(Number.isInteger(changed) ? { fields_changed_count: changed } : {}),
      });
      // A draft with a credit line is filed as לא תקין, not approved (#260).
      toast(r?.data?.status === 'לא תקין' ? 'יש זיכוי פתוח — המסמך נשמר כלא תקין' : 'הטיוטה אושרה');
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה באישור טיוטה', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleApproveAll = () => {
    if (approvable === 0 || filtering) return;
    const staying = waitingCount === 0 ? '' : waitingCount === 1
      ? ' טיוטה אחת שמחכה לטיפול תישאר ברשימה.'
      : ` ${waitingCount} טיוטות שמחכות לטיפול יישארו ברשימה.`;
    askConfirm({
      title: 'אישור כל הטיוטות',
      message: `לאשר את כל ${approvable} הטיוטות?`,
      detail: `כל הטיוטות יעברו לסטטוס "תקין" ויופיעו בדוחות. טיוטה עם זיכוי פתוח תישמר כ"לא תקין".${staying}`,
      confirmLabel: `אשר ${approvable} טיוטות`,
      onConfirm: doApproveAll,
    });
  };

  const doApproveAll = async () => {
    // One time for the batch, read at the confirm; the server shares it
    // across the drafts it approves.
    const review = { review_seconds: queueSeconds() };
    setBusy(true);
    try {
      const r = await documentsApi.approveAll({ review });
      // A draft with an opened credit is a decision too (saved as לא תקין).
      if ((r?.data?.approved || 0) + (r?.data?.saved_invalid_credit || 0) > 0) markAction();
      track(EVENTS.DOCUMENT_APPROVED, { source: 'drafts_bulk', bulk_count: r.data.approved, saved_invalid_credit: r.data.saved_invalid_credit || 0, queue_size: drafts.length });
      // The server deliberately holds back חשבונית מרכזת drafts so each one can
      // be offered its delivery-note links on approval (see the route). Say so —
      // a bulk action that leaves rows behind without explaining reads as a
      // failure, and the user's next move would be to click it again.
      const held = r.data.skipped_consolidated || 0;
      const waiting = (r.data.skipped_empty_returns || 0) + (r.data.skipped_return_pending || 0);
      const noSupplier = r.data.skipped_no_supplier || 0;
      const dupes = r.data.skipped_duplicates || 0;
      const creditInvalid = r.data.saved_invalid_credit || 0;
      const parts = [];
      if (r.data.approved > 0 || creditInvalid === 0) parts.push(`אושרו ${r.data.approved} טיוטות`);
      if (creditInvalid > 0) parts.push(`${creditInvalid} עם זיכוי פתוח נשמרו כלא תקין`);
      if (held > 0) parts.push(`${held} חשבוניות מרכזת נותרו לאישור פרטני`);
      if (waiting > 0) parts.push(waiting === 1 ? 'טיוטה אחת נשארה לטיפול (מסומנת ברשימה)' : `${waiting} נשארו לטיפול (מסומנות ברשימה)`);
      if (noSupplier > 0) parts.push(`${noSupplier} טיוטות ללא ספק נותרו — יש לבחור ספק`);
      if (dupes > 0) parts.push(`${dupes} טיוטות עם מספר מסמך שכבר קיים נותרו — יש לתקן את המספר או למחוק`);
      toast(parts.join(' · '));
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה באישור טיוטות', 'error');
    } finally {
      setBusy(false);
    }
  };

  // The reason dialog (components/DeleteDocumentDialog.jsx) owns the request
  // and its toast; this page only reloads once the draft is gone.
  const [deleting, setDeleting] = useState(null);
  const handleDelete = (d) => setDeleting({ ...d, status: 'טיוטה' });

  // The rows on screen, in the chosen order. By supplier, a supplier's drafts
  // read as one run: its first row carries the name in bold with a count and
  // a thin line above; the rest repeat the name quietly (on a phone the run's
  // first row is often off-screen).
  const visible = filterDraftQueue(sorted, filteringGate ? gateFilter : null).filter(d => !filteringDupes || d.duplicate_warning);
  const groupOf = (d) => (d.supplier_id ? String(d.supplier_id) : '');
  const runSize = (i) => {
    let n = 1;
    while (i + n < visible.length && groupOf(visible[i + n]) === groupOf(visible[i])) n++;
    return n;
  };

  // created_at comes from SQLite as 'YYYY-MM-DD HH:MM:SS' (UTC). Render in
  // local Hebrew format so the upload time matches what the user expects.
  const fmtUploaded = (s) => (s ? formatUploadedFull(s) || s : '—');

  return (
    <div>
      {/* אשר הכל acts on the whole queue, so it sits in the title row like
          every page-level action (design system, "Where buttons go"). */}
      <div className="page-title-row drafts-title-row">
        <h1 className="page-title">טיוטות ממתינות לאישור</h1>
        <div className="page-title-actions">
          {/* The queue order (#313): the app's switch, its rule behind the ?
              (owner, 22/10/2026). On by default. Only with 2+ supplier
              groups — with one there is nothing to choose. The text is not a
              <label>: a click on the ? inside one would flip the switch too;
              the switch carries the accessible name. */}
          {/* The gate's filters and the duplicates toggle, one group (owner
              answer 8: the duplicates toggle stays beside לתיקון), each in
              C2's soft sage while on. Only for a colour that has drafts. */}
          {(counts.red > 0 || counts.yellow > 0 || dupeCount > 0) && (
            <span className="drafts-filter-group" role="group" aria-label="סינון התור">
              {['red', 'yellow'].filter(f => counts[f] > 0).map(f => (
                <button
                  key={f}
                  type="button"
                  className={`btn btn-sm drafts-gate-filter${filteringGate && gateFilter === f ? ' is-on' : ''}`}
                  onClick={() => toggleGateFilter(f)}
                  aria-pressed={filteringGate && gateFilter === f}
                  title={f === 'red' ? 'הצג רק טיוטות שהבדיקה מצאה בהן מה לתקן' : 'הצג רק טיוטות שכדאי לבדוק בעיון'}
                >
                  <Icon name={GATE_MARKS[f].icon} size={14} /> {GATE_MARKS[f].label} {counts[f]}
                </button>
              ))}
              {dupeCount > 0 && (
                <button
                  type="button"
                  className={`btn btn-sm drafts-dupes-toggle${filteringDupes ? ' is-on' : ''}`}
                  onClick={toggleDupes}
                  aria-pressed={filteringDupes}
                  title="הצג רק טיוטות שזוהו ככפיל של מסמך קיים"
                >
                  <Icon name="alert" size={15} />
                  <span><span className="drafts-dupes-prefix">הצג רק </span>כפילים ({dupeCount})</span>
                </button>
              )}
            </span>
          )}
          {showOrderToggle && (
            <span className="drafts-order-switch">
              <Switch checked={bySupplier} onChange={setBySupplier} label="מיין לפי ספק" />
              <span className="drafts-order-switch-text" aria-hidden="true">מיין לפי ספק</span>
              <StatInfo ariaLabel="מה עושה מיין לפי ספק?">{ORDER_INFO}</StatInfo>
            </span>
          )}
          {/* A plain count, not a filter: a green filter beside אשר הכל would
              be stage 4 before the 1% bar is met. Stage 4 turns this slot
              into its own button. */}
          {counts.green > 0 && <span className="drafts-gate-count" title={GREEN_TITLE}>{counts.green === 1 ? '1 תקין' : `${counts.green} תקינים`}</span>}
          {/* אשר הכל approves the WHOLE queue, hidden rows included, so it is
              off while a gate filter hides some (spec §2). The tooltip sits
              on a wrapper: a disabled button fires no hover. */}
          <span title={filtering ? APPROVE_ALL_FILTERED_TITLE : undefined} className="drafts-approve-all">
            <button
              className="btn btn-sm btn-success"
              onClick={handleApproveAll}
              disabled={busy || approvable === 0 || filtering}
            >
              <Icon name="check" size={16} /> אשר הכל ({approvable})
            </button>
          </span>
          {/* ⋮ is always last (design system, "Where buttons go"). Only for a
              login the server lets preview the gate before go-live. */}
          {previewAllowed && (
            <div className="page-menu">
              <button
                type="button"
                className="page-menu-trigger"
                aria-label="פעולות לתור"
                aria-expanded={pageMenuOpen}
                onClick={() => setPageMenuOpen(o => !o)}
              >⋮</button>
              {pageMenuOpen && (
                <>
                  <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                  <div className="page-menu-panel">
                    <button
                      type="button"
                      role="menuitemcheckbox"
                      aria-checked={gatePreview}
                      className="page-menu-item"
                      title="תצוגה מקדימה לך בלבד — שאר הסוקרים לא רואים צבעים עד העלייה לאוויר"
                      onClick={() => { setPageMenuOpen(false); toggleGatePreview(); }}
                    >
                      <Icon name={gatePreview ? 'eye-off' : 'eye'} size={15} /> {gatePreview ? 'הסתר צבעי בדיקה' : 'הצג צבעי בדיקה'}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את הטיוטות" onRetry={load} />
        ) : drafts.length === 0 ? (
          <EmptyState message="אין טיוטות ממתינות" />
        ) : (
          <div className="table-wrapper table-scroll-mobile drafts-table">
            <table>
              <thead>
                <tr>
                  <th className="drafts-gate-col">בדיקה</th>
                  <th>ספק</th>
                  <th>סוג מסמך</th>
                  <th>מספר מסמך</th>
                  <th>תאריך</th>
                  <th>סכום</th>
                  <th>תאריך העלאה</th>
                  <th className="drafts-actions-col">פעולות</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d, i) => {
                  const missingSupplier = !d.supplier_id;
                  const isDupe = !!d.duplicate_warning;
                  const groupStart = bySupplier && (i === 0 || groupOf(visible[i - 1]) !== groupOf(d));
                  const groupCount = groupStart ? runSize(i) : 0;
                  // No row tint (#332): the gate mark is the row's only status
                  // colour (brain 08). The tints disagreed with the gate anyway
                  // — no supplier is red in the gate, amber as a tint.
                  return (
                  <tr key={d.id} className={`row-clickable${groupStart && i > 0 ? ' drafts-group-start' : ''}`} onClick={() => openReview(d)}>
                    <td className="drafts-gate-col"><GateMark gate={d} /></td>
                    <td className={bySupplier && !groupStart && !missingSupplier ? 'drafts-group-rest' : undefined}
                        style={{ fontWeight: bySupplier && !groupStart ? 400 : 600 }}>
                      {missingSupplier
                        // Plain content: the row's red mark already says it needs a
                        // fix, and a second, amber status colour in the row
                        // disagreed with it (the long version wrapped to five
                        // lines on a phone; #332 design review).
                        ? <span className="drafts-no-supplier">לא זוהה ספק</span>
                        : (d.supplier_name || '—')}
                      {groupCount > 1 && <span className="drafts-group-count"> ({groupCount})</span>}
                    </td>
                    <td><span className="badge badge-blue">{d.doc_type}</span></td>
                    <td style={{ color: 'var(--ink-muted)' }}>
                      {d.doc_num || '—'}
                      {isDupe && (
                        <span
                          className="badge badge-compact badge-red drafts-dupe-pill"
                          title="המספר הזה כבר קיים כמסמך מאושר אצל הספק. יש למחוק או לעדכן את מספר המסמך לפני אישור."
                        >
                          <Icon name="alert" size={12} /> כפיל
                        </span>
                      )}
                      {d.empty_return ? (
                        <span className="badge badge-compact badge-gray drafts-dupe-pill" title="אפשר להוסיף שורות או לאשר בנפרד. אשר הכל לא מאשר אותה.">ללא שורות</span>
                      ) : null}
                      {d.return_pending ? (
                        <span className="badge badge-compact badge-gray drafts-dupe-pill" title="במסמך יש גם תעודת החזרה. אפשר ליצור אותה מהמסמך או לאשר בנפרד. אשר הכל לא מאשר אותה.">חסרה החזרה</span>
                      ) : null}
                    </td>
                    <td>{formatDateShort(d.date) || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{formatCurrency(d.total)}</td>
                    <td style={{ color: '#666', fontSize: '0.85rem' }}>{fmtUploaded(d.created_at)}</td>
                    <td className="drafts-actions-col" onClick={e => e.stopPropagation()}>
                      <div className="drafts-row-actions">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => openReview(d)}
                          disabled={busy}
                        ><Icon name="edit" size={15} /> ערוך ואשר</button>
                        <div className="page-menu">
                          <button
                            type="button"
                            className="page-menu-trigger row-menu-trigger"
                            aria-label="פעולות נוספות"
                            aria-expanded={rowMenu?.id === d.id}
                            onClick={e => toggleRowMenu(d.id, e.currentTarget)}
                          >⋮</button>
                          {rowMenu?.id === d.id && (
                            <>
                              <div className="menu-backdrop" onClick={() => setRowMenu(null)} />
                              <div className="page-menu-panel" style={rowMenu.style}>
                                {/* Per-row אשר requires a supplier; if the AI didn't
                                    identify one, the admin must open the doc and pick
                                    one first. The "ערוך ואשר" path covers that. */}
                                <button
                                  type="button"
                                  className="page-menu-item"
                                  style={{ color: 'var(--success-ink)', fontWeight: 600 }}
                                  onClick={() => { setRowMenu(null); handleApprove(d); }}
                                  disabled={busy || missingSupplier}
                                  title={missingSupplier
                                    ? 'בחר ספק קודם דרך "ערוך ואשר"'
                                    : d.doc_type === 'חשבונית מרכזת'
                                      ? 'מרכזת נפתחת לאישור במסך המסמך כדי לקשר תעודות משלוח'
                                      : ''}
                                ><Icon name="check" size={15} /> אשר</button>
                                <button type="button" className="page-menu-item" onClick={() => { setRowMenu(null); openReview(d); }} disabled={busy}>
                                  <Icon name="edit" size={15} /> ערוך ואשר
                                </button>
                                {isDupe && (
                                  <button type="button" className="page-menu-item" onClick={() => { setRowMenu(null); openCompare(d); }} disabled={busy}>
                                    <Icon name="eye" size={15} /> השווה זה ליד זה
                                  </button>
                                )}
                                <div className="page-menu-divider" />
                                <button type="button" className="page-menu-item" style={{ color: 'var(--danger-ink)' }} onClick={() => { setRowMenu(null); handleDelete(d); }} disabled={busy}>
                                  <Icon name="trash" size={15} /> מחק
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {confirmDialog}
      {deleting && (
        <DeleteDocumentDialog
          doc={deleting}
          title="מחיקת טיוטה"
          confirmLabel="מחק טיוטה"
          successMessage="הטיוטה נמחקה"
          initialReason={deleting.fromCompare ? 'duplicate' : ''}
          review={() => ({ review_seconds: queueSeconds() })}
          onCancel={() => setDeleting(null)}
          onDeleted={() => { markAction(); setDeleting(null); load(); }}
        />
      )}
      {deletingExisting && (
        <DeleteDocumentDialog
          doc={deletingExisting.existing}
          title="מחיקת המסמך הקיים"
          message={keepDraftDeleteQuestion(deletingExisting.existing, formatCurrency)}
          context={keepDraftDeleteContext(deletingExisting.draft.id)}
          docLabel="המסמך הקיים"
          warning={keepDraftDeleteWarning(deletingExisting.existing, formatCurrency)}
          confirmLabel="מחק את הקיים"
          successMessage={`המסמך הקיים #${deletingExisting.existing.id} נמחק`}
          initialReason="duplicate"
          onCancel={() => setDeletingExisting(null)}
          onDeleted={() => {
            const d = deletingExisting.draft;
            setDeletingExisting(null);
            navigate(`/document/${d.id}`, { state: { autoEdit: true, autoOpenFiles: true, entry: 'queue_edit' } });
          }}
        />
      )}
      {comparing && (
        <DuplicateCompareDialog
          draft={comparing.draft}
          existing={comparing.draft.duplicate_of}
          onClose={() => setComparing(null)}
          onDelete={() => { const d = comparing.draft; setComparing(null); setDeleting({ ...d, fromCompare: true }); }}
          onKeepDraft={() => { const d = comparing.draft; setComparing(null); setDeletingExisting({ draft: d, existing: d.duplicate_of }); }}
          onChangeNumber={() => {
            const d = comparing.draft;
            setComparing(null);
            navigate(`/document/${d.id}`, { state: { autoEdit: true, autoOpenFiles: true, entry: 'queue_edit', focusDocNum: true } });
          }}
        />
      )}
    </div>
  );
}
